import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import { Button } from '../Button/Button';
import { ButtonTier } from '../Button/ButtonTier.csharp';
import { ButtonType } from '../Button/ButtonType.csharp';
import { SvgIcon } from '../SvgIcon/SvgIcon';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import styles from './FactBox.module.css';
import type { FactBoxColorVariant } from './FactBoxColorVariant';

interface FactBoxTranslations {
    open: string;
    close: string;
}

export interface FactBoxProps {
    variant: FactBoxColorVariant;
    heading: string;
    content: HtmlString;
    translations: FactBoxTranslations;
}

export const FactBox: React.FC<React.PropsWithChildren<FactBoxProps>> = props => {
    const theme = useTheme();
    const { variant = 'light', translations, heading, content } = props;

    const { withRoundedCorners } = theme;
    const [expanded, setExpanded] = React.useState(false);
    const factBoxId = uniqueId('FactBox__');
    const richtextVariant = variant === 'light' ? 'dark' : 'light';

    const toggleExpanded = () => {
        setExpanded(!expanded);
    };

    // KNOWN ISSUE: FactBox is not prepared to support shorter text (the button to expand/collapse is always visible).
    // We are aware of it, but we assume editors will use this box to present longer text.
    // So, unless the client reports this, we should not bother fixing it.

    return (
        <div
            className={classNames(
                styles.FactBox,
                styles[`Variant--${variant}`],
                expanded && styles['Variant--expanded'],
                withRoundedCorners && styles['Variant--rounded']
            )}
        >
            <h3 className={styles.Heading}>{heading}</h3>
            <div className={styles.Content}>
                <div className={styles.RichtextAdjustments} id={factBoxId}>
                    <HtmlDiv
                        html={content}
                        className={classNames('RichtextContent', `RichtextContent--${richtextVariant}`)}
                    />
                </div>
            </div>
            <Button
                onClick={toggleExpanded}
                aria-expanded={expanded}
                aria-controls={factBoxId}
                type={ButtonType.Outline}
                tier={variant === 'light' ? ButtonTier.Primary : ButtonTier.Tertiary}
                theme={theme}
                className={styles.Button}
            >
                <span className={styles.Label}>{expanded ? translations.close : translations.open}</span>
                <SvgIcon iconName="chevron-down" className={styles.Icon} />
            </Button>
        </div>
    );
};
